import { commitOptions } from '../actions/commitOptions';
import { updateSettingDirectly } from '../actions/updateSettingDirectly';
import { updateUserOptions } from '../actions/updateUserOptions';
import UserOptions from '../store/schema/UserOptions';
import getStore from '../store/store';
import serializeOverriddenOptions from '../utils/serializeOverriddenOptions';
import { logUsage } from 'owa-analytics';
import * as trace from 'owa-trace';
import { orchestrator } from 'satcheljs';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    ProofingOptions,
    lazyCreateOrUpdateOptionsForFeature,
    ComposeAssistanceOptions,
} from 'owa-outlook-service-options';
import { assertNever } from 'owa-assert';

orchestrator(commitOptions, actionMessage => {
    try {
        const store = getStore();

        // Make service call to save overridden options
        saveUpdatedOptions(store.settingsDialogViewState);

        // Update the store for overridden options
        updateUserOptions(store.settingsDialogViewState);
    } catch (e) {
        trace.errorThatWillCauseAlert(
            `[owa-proofing-options] Error committing changes: ${e.message}`
        );
    }
});

orchestrator(updateSettingDirectly, actionMessage => {
    let updatedUserOption: UserOptions;
    switch (actionMessage.settingName) {
        case 'spelling':
            updatedUserOption = { spellCheckEnabled: actionMessage.newValue as boolean };
            break;
        case 'grammar':
            updatedUserOption = { grammarEnabled: actionMessage.newValue as boolean };
            break;
        case 'refinements':
            updatedUserOption = { writingRefinementsEnabled: actionMessage.newValue as boolean };
            break;
        case 'locale':
            updatedUserOption = { proofingLocale: actionMessage.newValue as string };
            break;
        case 'textPredictionEnabled':
            updatedUserOption = { textPredictionEnabled: actionMessage.newValue as boolean };
            break;
        case 'finalDatePremiumDemo':
            updatedUserOption = { finalDatePremiumDemo: actionMessage.newValue as string };
            break;
        case 'premiumSuggestionsCount':
            updatedUserOption = { premiumSuggestionsCount: actionMessage.newValue as number };
            break;
        case 'startDatePremiumPreview':
            updatedUserOption = { startDatePremiumPreview: actionMessage.newValue as string };
            break;
        case 'weekCounter':
            updatedUserOption = { weekCounter: actionMessage.newValue as number };
            break;
        default:
            assertNever(actionMessage.settingName);
    }

    saveUpdatedOptions(updatedUserOption);
    updateUserOptions(updatedUserOption);
});

function saveUpdatedOptions(userOptions: UserOptions) {
    const {
        overriddenOptions,
        overriddenCategories,
        spellCheckEnabled,
        grammarEnabled,
        proofingLocale,
        textPredictionEnabled,
        writingRefinementsEnabled,
        finalDatePremiumDemo,
        premiumSuggestionsCount,
        startDatePremiumPreview,
        weekCounter,
    } = userOptions;

    const proofingOptions = getOptionsForFeature<ProofingOptions>(OwsOptionsFeatureType.Proofing);
    const newProofingOptions = { ...proofingOptions };

    if (overriddenOptions !== undefined && overriddenCategories !== undefined) {
        const stringifiedOptions = serializeOverriddenOptions(
            overriddenOptions,
            overriddenCategories
        );
        newProofingOptions.overriddenOptions = stringifiedOptions;
    }

    if (spellCheckEnabled !== undefined) {
        newProofingOptions.spellCheckEnabled = spellCheckEnabled;
    }

    if (grammarEnabled !== undefined) {
        newProofingOptions.grammarEnabled = grammarEnabled;
    }

    if (writingRefinementsEnabled !== undefined) {
        newProofingOptions.writingRefinementsEnabled = writingRefinementsEnabled;
    }

    if (proofingLocale !== undefined) {
        newProofingOptions.proofingLocale = proofingLocale;
    }

    if (finalDatePremiumDemo !== undefined) {
        newProofingOptions.finalDatePremiumDemo = finalDatePremiumDemo;
    }

    if (premiumSuggestionsCount !== undefined) {
        newProofingOptions.premiumSuggestionsCount = premiumSuggestionsCount;
    }

    if (startDatePremiumPreview !== undefined) {
        newProofingOptions.startDatePremiumPreview = startDatePremiumPreview;
    }

    if (weekCounter !== undefined) {
        newProofingOptions.weekCounter = weekCounter;
    }

    lazyCreateOrUpdateOptionsForFeature.importAndExecute(
        OwsOptionsFeatureType.Proofing,
        newProofingOptions
    );

    if (textPredictionEnabled !== undefined) {
        const composeAssistanceOptions = getOptionsForFeature<ComposeAssistanceOptions>(
            OwsOptionsFeatureType.ComposeAssistance
        );

        const newCAOptions = { ...composeAssistanceOptions };
        newCAOptions.composeAssistanceEnabled = textPredictionEnabled;
        lazyCreateOrUpdateOptionsForFeature.importAndExecute(
            OwsOptionsFeatureType.ComposeAssistance,
            newCAOptions
        );

        // Log ComposeAssistanceSettingDisabled datapoint if the value of textPrediction changed and it is disabled
        if (
            !textPredictionEnabled &&
            textPredictionEnabled !== composeAssistanceOptions.composeAssistanceEnabled
        ) {
            logUsage('ComposeAssistance_SettingDisabled', { logEvery: 1 });
        }
    }
}
